import * as v from 'valibot';
import { ActionFunctionArgs, LoaderFunctionArgs } from '@remix-run/cloudflare';
import { user_anonymous } from '~/lib/auth';
import FormGroup from '~/components/form/FormGroup';
import ValibotForm from '~/components/valibot/ValibotForm';
import ValibotFieldInput from '~/components/valibot/ValibotFieldInput';
import Heading from '~/components/layout/Heading';
import ValibotButton from '~/components/valibot/ValibotButton';
import { useActionData } from '@remix-run/react';
import Message from '~/components/Message';
import { db_user_by_email } from '~/.server/drizzle/repository/user';
import { clean_email } from '~/.server/drizzle/repository';
import { remix_schema_post } from '~common/.server/remix';
import { password_reset_allow } from '~/.server/password-reset-throttle';
import { password_link_for_forgot, password_link_recent } from '~/.server/provider-password-link';
import { FORGOT_LIMITED_MESSAGE, FORGOT_SENT_MESSAGE } from '~/lib/password-link';

const schema = v.object({
  email: v.pipe(v.string('Please provide an email address.'), v.email('The email address you provided is not valid.')),
});

export async function loader(args: LoaderFunctionArgs) {
  await user_anonymous(args);

  return {};
}

export async function action(args: ActionFunctionArgs) {
  const data = await remix_schema_post(args, schema);
  const email = clean_email(data.email);

  // Counted for every address, account or not (.server/password-reset-throttle.ts).
  if (!(await password_reset_allow(args, { email, ip: args.request.headers.get('CF-Connecting-IP') }))) {
    return { success: false, error: FORGOT_LIMITED_MESSAGE };
  }

  // The lookup, the write and the Inngest send run AFTER the answer
  // (waitUntil, review of #544): an address with an account and one without
  // are answered in the same time, so timing does not say which is which.
  const request = async () => {
    try {
      const user = await db_user_by_email(args, email);

      if (!user || user.disabled) {
        return;
      }

      // A photographer's set-password link sent minutes ago stays the live one:
      // asking again does not replace it (the email with it is in the inbox).
      if (password_link_for_forgot(user) && password_link_recent(user.passwordLink)) {
        return;
      }

      await user.db_patch({ password_change: true, reset: true, notified_password: false });

      await user.dirty_notify();
    } catch (error) {
      console.error(
        JSON.stringify({
          message: 'Forgot password request failed after the answer.',
          error: error instanceof Error ? error.message : String(error),
        }),
      );
    }
  };

  const waitUntil = args.context.ctx?.waitUntil?.bind(args.context.ctx);

  if (waitUntil) {
    waitUntil(request());
  } else {
    await request();
  }

  return { success: true, error: null };
}

export default function () {
  const response = useActionData<typeof action>();

  if (response?.success) {
    return (
      <div className="mb-6">
        <Message type="success">{FORGOT_SENT_MESSAGE}</Message>
      </div>
    );
  }

  return (
    <>
      <div className="pb-6 text-left">
        <Heading title="Forgot Password" size="xl" />
      </div>
      <ValibotForm schema={schema}>
        <FormGroup>
          {response?.error && <Message type="error">{response.error}</Message>}
          <ValibotFieldInput name="email" label="E-mail Address" autoFocus />
          <ValibotButton variant="tenant" showError>
            Send Reset Link
          </ValibotButton>
        </FormGroup>
      </ValibotForm>
    </>
  );
}
